import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  DropdownAlign,
  DropdownContentProps,
  DropdownPosition,
  DropdownProps,
  DropdownTriggerProps,
} from './types';

const positionClasses: Record<DropdownPosition, string> = {
  top: 'dropdown-top',
  bottom: 'dropdown-bottom',
  left: 'dropdown-left',
  right: 'dropdown-right',
};

const alignClasses: Record<DropdownAlign, string> = {
  start: 'dropdown-start',
  center: 'dropdown-center',
  end: 'dropdown-end',
};

const DropdownBase = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      className,
      position,
      align,
      hover = false,
      open = false,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'dropdown',
      position && positionClasses[position],
      align && alignClasses[align],
      hover && 'dropdown-hover',
      open && 'dropdown-open',
      className,
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  },
);

const Trigger = forwardRef<HTMLDivElement, DropdownTriggerProps>(
  ({ className, children, disabled, ...props }, ref) => (
    <div
      ref={ref}
      tabIndex={0}
      role="button"
      aria-disabled={disabled}
      className={twMerge(
        className,
        disabled && 'pointer-events-none opacity-50',
      )}
      {...props}
    >
      {children}
    </div>
  ),
);

const Content = forwardRef<HTMLDivElement, DropdownContentProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      tabIndex={-1}
      className={twMerge('dropdown-content', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

DropdownBase.displayName = 'Dropdown';
Trigger.displayName = 'Dropdown.Trigger';
Content.displayName = 'Dropdown.Content';

export const Dropdown = Object.assign(DropdownBase, {
  Trigger,
  Content,
});
